import { Box, ButtonBase, Typography } from '@mui/material';
import { JSX, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    BODY_DIAGRAM_HEIGHT,
    BODY_DIAGRAM_OUTLINE_SRC,
    BODY_DIAGRAM_WIDTH,
    drawStrokes,
    Stroke,
} from '../../library/bodyDiagramHelper';
import ResourcesIds from '../../resources/ResourcesIds';
import { BodyDiagramDialog } from './BodyDiagramDialog';
import { hintStyles, thumbnailButtonStyles, thumbnailLayerStyles, wrapperStyles } from './bodyDiagramField.styles';

interface IBodyDiagramFieldProps {
    value: Stroke[];
    onChange: (strokes: Stroke[]) => void;
}

/** Half the artwork: sharp at the thumbnail's 240px on a 2x screen, and cheap to redraw. */
const THUMBNAIL_SCALE = 0.5;

/**
 * The body diagram as it sits in the form: a thumbnail showing what has been marked so far,
 * which opens the full-screen `BodyDiagramDialog` to draw.
 *
 * <p>Optional, like everything it prints next to: most sheets carry no drawing, and one
 * without it prints the bare outline exactly as it always has.
 */
export const BodyDiagramField = ({ value, onChange }: IBodyDiagramFieldProps): JSX.Element => {
    const { t: translate } = useTranslation();
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [open, setOpen] = useState<boolean>(false);

    // Sized here rather than through `width`/`height` on the element -- see the dialog's
    // own redraw for why those two never reach the canvas.
    useEffect(() => {
        const canvas: HTMLCanvasElement | null = canvasRef.current;

        if (!canvas) {
            return;
        }

        canvas.width = BODY_DIAGRAM_WIDTH * THUMBNAIL_SCALE;
        canvas.height = BODY_DIAGRAM_HEIGHT * THUMBNAIL_SCALE;

        drawStrokes(canvas, value);
    }, [value]);

    const handleDone = (strokes: Stroke[]): void => {
        onChange(strokes);
        setOpen(false);
    };

    const drawn: boolean = value.length > 0;

    return (
        <Box sx={wrapperStyles}>
            <ButtonBase
                sx={thumbnailButtonStyles}
                onClick={(): void => setOpen(true)}
                aria-label={translate(
                    drawn ? ResourcesIds.EVALUATION_BODY_DIAGRAM_EDIT : ResourcesIds.EVALUATION_BODY_DIAGRAM_DRAW
                )}
            >
                <Box
                    component="img"
                    src={BODY_DIAGRAM_OUTLINE_SRC}
                    alt={translate(ResourcesIds.EVALUATION_BODY_DIAGRAM_ALT)}
                    sx={thumbnailLayerStyles}
                />
                <Box
                    component="canvas"
                    ref={canvasRef}
                    sx={thumbnailLayerStyles}
                />
            </ButtonBase>

            {/* The thumbnail itself is the only way in -- a button beside it was a second
                control for one action, on a picture nobody would fail to tap. This line is
                therefore the only thing that says the picture is live, so it stays whether
                or not anything has been marked on it. */}
            <Typography sx={hintStyles}>
                {translate(ResourcesIds.EVALUATION_BODY_DIAGRAM_HINT)}
            </Typography>

            <BodyDiagramDialog
                open={open}
                strokes={value}
                onCancel={(): void => setOpen(false)}
                onDone={handleDone}
            />
        </Box>
    );
};
